/**
 * Shared display helpers for SdI/FatturaPA UI.
 *
 * Single source of truth for the human-readable labels that were previously
 * duplicated (or printed raw as English enums) across Dashboard, Invoice
 * detail, Commercialista, etc. All copy lives in the i18n catalog so the UI
 * stays fully Italian-by-default while remaining translatable.
 */

import { tImmediate } from '../i18n/index.js';
import { isExpectedMancataConsegna } from './expected-mancata-consegna.js';

// Status buckets drive the badge colour. Kept here so every screen colours the
// same status identically (previously each screen had its own copy of this).
//
// The three lists together are exactly the InvoiceStatus enum in
// src/domain/state-machine.js — nothing more. Values that no transition can
// ever produce ('rejected', 'send_failed', 'notification_error', 'accepted')
// used to sit in here and in the Dashboard's private copy: they coloured
// nothing, matched nothing, and made the "da controllare" counter miss the
// statuses that do occur.
export const STATUS_COMPLETED = [
  'delivered', 'completed', 'decorrenza_termini',
  'esito_cedente_accettato', 'esito_sent', 'received_processed', 'imported',
];
export const STATUS_FAILED = [
  'rejected_by_transport', 'scarto', 'notifica_mancata_consegna',
  'esito_cedente_rifiutato', 'error', 'received_rejected', 'esito_scarto',
];
export const STATUS_PENDING = [
  'draft', 'created', 'xml_generated', 'queued_for_send', 'sent',
  'accepted_by_transport', 'sdi_received', 'received',
];

/**
 * True when the status means "questa fattura non è andata a buon fine e
 * qualcuno deve intervenire" — the single definition behind every red badge
 * and every "da controllare" counter.
 */
export function isFailedStatus(status) {
  return STATUS_FAILED.includes(status);
}

/**
 * Come isFailedStatus(), ma guardando la fattura intera: la mancata consegna di
 * una fattura senza codice destinatario né PEC è l'esito previsto (la fattura è
 * nell'area riservata del cliente) e non va contata fra quelle da controllare.
 * Ogni contatore e filtro "da controllare" passa da qui.
 */
export function isFailedInvoice(invoice) {
  return isFailedStatus(invoice?.status) && !isExpectedMancataConsegna(invoice);
}

/**
 * True for a status that is not finished yet — includes invoices still sitting
 * in this system, before any transmission. Drives the yellow badge.
 * For "SdI owes us a ricevuta" use isAwaitingSdiOutcome() below instead.
 */
export function isPendingStatus(status) {
  return STATUS_PENDING.includes(status);
}

/**
 * Statuses that mean "già trasmessa a SdI, ricevuta non ancora arrivata".
 *
 * Deliberately narrower than STATUS_PENDING: 'created', 'xml_generated' and
 * 'queued_for_send' are invoices that never left this system, so counting them
 * under "Trasmissioni in attesa" would tell the user SdI owes them an answer to
 * a message it was never sent. This list is the frontend equivalent of the
 * SdiTransmission rows with status='pending' that the old cross-tenant
 * /api/admin/transmissions/pending returned.
 */
export const STATUS_AWAITING_SDI = ['sent', 'accepted_by_transport', 'sdi_received'];

/** True while SdI still owes a ricevuta (consegna, scarto o mancata consegna). */
export function isAwaitingSdiOutcome(status) {
  return STATUS_AWAITING_SDI.includes(status);
}

/** Italian label for an invoice status enum, with a safe fallback. */
export function statusLabel(status) {
  if (!status) return tImmediate('invoices.status.unknown');
  return tImmediate(
    `invoices.status.${status}`,
    undefined,
    tImmediate('invoices.status.unknown'),
  );
}

/** Badge CSS class for a status (consistent colour everywhere). */
export function statusBadgeClass(status) {
  if (STATUS_COMPLETED.includes(status)) return 'badge-green';
  if (STATUS_FAILED.includes(status)) return 'badge-red';
  if (STATUS_PENDING.includes(status)) return 'badge-yellow';
  return 'badge-cyan';
}

/** Plain-Italian explanation of a status (empty string when none defined). */
export function statusTooltip(status) {
  if (!status) return tImmediate('invoices.statusHelp.unknown', undefined, '');
  return tImmediate(`invoices.statusHelp.${status}`, undefined, '');
}

/**
 * Italian label for an SdI notification type.
 *
 * The backend stores the snake_case identifiers it dispatches on
 * (src/sdi/inbound-handler.js) — `notifica_scarto`, `ricevuta_consegna`, … —
 * and the API returns the entity untouched. The two-letter SdI codes (NS, RC,
 * …) are kept as aliases because they are what the paper documentation uses
 * and what an imported row may carry.
 */
export function notificationTypeLabel(type) {
  if (!type) return tImmediate('app.noData');
  return tImmediate(`invoices.notifications.types.${type}`, undefined, type);
}

/**
 * Two-letter SdI code for a notification type, for the reader who is holding
 * the Agenzia delle Entrate documentation next to the screen. Empty when the
 * value is already a code (or is unknown), so nothing is printed twice.
 */
const NOTIFICATION_SDI_CODES = {
  ricevuta_consegna: 'RC',
  notifica_mancata_consegna: 'MC',
  notifica_scarto: 'NS',
  notifica_esito_cedente: 'NE',
  notifica_decorrenza_termini: 'DT',
  attestazione_trasmissione: 'AT',
  scarto_esito_committente: 'SE',
};

export function notificationSdiCode(type) {
  return NOTIFICATION_SDI_CODES[type] || '';
}

/**
 * "test" / "production" / "development" are what the server calls its
 * environments; nobody issuing an invoice should have to read English enums to
 * find out whether the invoice they just sent was real.
 */
export function environmentLabel(env) {
  if (!env) return tImmediate('app.noData');
  return tImmediate(`invoices.environments.${env}`, undefined, env);
}

/**
 * FPR12 / FPA12 is the FatturaPA transmission format: which of the two XSDs
 * the file follows, i.e. whether the invoice goes to a business or to the
 * Pubblica Amministrazione. Shown as "FPA12 · Verso la PA".
 */
export function formatoLabel(formato) {
  if (!formato) return tImmediate('app.noData');
  const name = tImmediate(`invoices.formats.${formato}`, undefined, '');
  return name ? `${formato} · ${name}` : formato;
}

/** Human direction label: outbound → "Emessa", inbound → "Ricevuta". */
export function directionLabel(dir) {
  if (dir === 'outbound') return tImmediate('common.directionOut');
  if (dir === 'inbound') return tImmediate('common.directionIn');
  return tImmediate('app.na');
}

/** it-IT euro formatting, tolerant of null/empty/non-numeric. */
export function formatEuro(n) {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) {
    return tImmediate('app.noData');
  }
  return Number(n).toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** it-IT date+time formatting, tolerant of null. */
export function formatDateTime(d) {
  if (!d) return tImmediate('app.noData');
  return new Date(d).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/** it-IT date-only formatting, tolerant of null. */
export function formatDate(d) {
  if (!d) return tImmediate('app.noData');
  return new Date(d).toLocaleDateString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

/**
 * Wrap a message we wrote ourselves — an Italian sentence out of the catalog —
 * so friendlyError() shows it verbatim instead of trying to classify it. Use
 * for client-side validation and for typed backend codes already translated at
 * the call site.
 *
 * @param {string} message an already-localized, user-facing sentence
 * @returns {Error}
 */
export function localizedError(message) {
  const err = new Error(message);
  err.localized = true;
  return err;
}

/**
 * HTTP status → the Italian sentence that explains it to someone who has no
 * idea what a status code is. Classifying on the status is the reliable half of
 * the job: the API answers in Italian prose, so matching keywords against the
 * message only ever worked by accident.
 */
const STATUS_KEYS = {
  400: 'errors.validation',
  401: 'errors.unauthorized',
  403: 'errors.forbidden',
  404: 'errors.notFound',
  409: 'errors.conflict',
  413: 'errors.tooLarge',
  422: 'errors.validation',
  429: 'errors.tooManyRequests',
  503: 'errors.unavailable',
};

/**
 * Map a raw error (Error | string | API message | HTTP code) to a friendly
 * Italian message plus the original technical detail for an opt-in <details>.
 * Returns { message, detail }.
 */
export function friendlyError(err) {
  const message = (typeof err === 'string' ? err : err?.message) || '';
  const s = message.toLowerCase();
  const status = typeof err?.status === 'number' ? err.status : null;

  // A FatturaPA validation failure already arrives as a full, Italian
  // explanation plus the list of everything wrong with the invoice. Replacing
  // that with a generic "some data is invalid" would throw away the only part
  // that tells the user what to fix, so it is shown as it came.
  if (err?.code === 'invalid_fatturapa') {
    return {
      message: message || tImmediate('errors.validation'),
      detail: Array.isArray(err.details) ? err.details.join('\n') : '',
      detailLabel: tImmediate('errors.invoiceProblems'),
    };
  }

  // Already an Italian sentence written for this exact situation — there is
  // nothing to classify and nothing to hide in a <details>.
  if (err?.localized === true && message) {
    return { message, detail: '' };
  }

  // No status at all means the request never reached the server: fetch() only
  // rejects on a transport failure.
  if (status === null && /failed to fetch|networkerror|load failed|err_connection|fetch failed/.test(s)) {
    return { message: tImmediate('errors.network'), detail: message };
  }

  let key = null;
  if (status !== null) {
    key = STATUS_KEYS[status] || (status >= 500 ? 'errors.server' : 'errors.generic');
  } else {
    // A bare string or a plain Error thrown by client-side code — no status to
    // read, so the old keyword sniffing is the only thing left to try.
    if (/http 401|unauthor|not authenticated|sessione/.test(s)) key = 'errors.unauthorized';
    else if (/http 403|forbidden|permess|not allowed/.test(s)) key = 'errors.forbidden';
    else if (/http 404|not found|non trovat/.test(s)) key = 'errors.notFound';
    else if (/http 409|conflict|già esist|duplicate/.test(s)) key = 'errors.conflict';
    else if (/http 4(00|22)|validation|invalid|non valid|required|obbligator/.test(s)) key = 'errors.validation';
    else if (/http 5\d\d|internal server|server error/.test(s)) key = 'errors.server';
    else key = 'errors.generic';
  }

  // The backend writes its errors for the person reading them, and they say
  // far more than "controlla i campi e riprova" ever could — which invoice,
  // which field, what to do next. When one arrived, it IS the headline; the
  // generic sentence for the status becomes the collapsed detail instead.
  // A 401 is the exception: the session is gone, the server's wording of that
  // ('unauthorised') is not for a user, and the app is already on its way back
  // to the login screen.
  if (err?.serverMessage && message && status !== 401) {
    return { message, detail: tImmediate(key) };
  }

  return { message: tImmediate(key), detail: message };
}
